/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";
import { useNavigate, useParams } from "react-router-dom";

const actionsColorMap = [
  "#1E8E3A", "#0A84FF", "#C62828", "#B8960A",
  "#8E44AD", "#E67E22", "#00BCD4", "#E91E63", "#3F51B5", "#7CB342",
];
const actionsGPIOMap = [5, 18, 19, 23];
const buttonLabels = ["1", "2", "3", "4"];

function CreateEventPage() {
  const { t } = useTranslation();
  const { id: eventId } = useParams();
  const isEditMode = !!eventId;

  const CATEGORY_OPTIONS = [
    { value: "", label: t("common.noCategory") },
    { value: "work", label: t("categories.work") },
    { value: "health", label: t("categories.health") },
    { value: "leisure", label: t("categories.leisure") },
    { value: "family", label: t("categories.family") },
    { value: "self-care", label: t("categories.selfCare") },
    { value: "chores", label: t("categories.chores") },
    { value: "other", label: t("categories.other") },
  ];

  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [actions, setActions] = useState([
    { name: "", typeId: actionsGPIOMap[0], active: false, timed: false },
    { name: "", typeId: actionsGPIOMap[1], active: false, timed: false },
    { name: "", typeId: actionsGPIOMap[2], active: false, timed: false },
    { name: "", typeId: actionsGPIOMap[3], active: false, timed: false },
  ]);
  const [customActions, setCustomActions] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateIcon, setTemplateIcon] = useState("📋");
  const [templatePublic, setTemplatePublic] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const navigate = useNavigate();

  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchEvent = async () => {
      try {
        const event = await EventsService.getEvent(eventId);
        setEventName(event.name);
        setIsStarted(!!event.started);
        if (event.category) setCategory(event.category);
        if (event.tags) setTags(event.tags);
        const loaded = [
          { name: "", typeId: actionsGPIOMap[0], active: false, timed: false },
          { name: "", typeId: actionsGPIOMap[1], active: false, timed: false },
          { name: "", typeId: actionsGPIOMap[2], active: false, timed: false },
          { name: "", typeId: actionsGPIOMap[3], active: false, timed: false },
        ];
        const loadedCustom = [];
        (event.actions || []).forEach((a) => {
          if (String(a.typeId).startsWith("custom-")) {
            loadedCustom.push({ name: a.name, typeId: a.typeId, timed: !!a.timed });
          } else {
            const idx = actionsGPIOMap.indexOf(Number(a.typeId));
            if (idx !== -1) {
              loaded[idx] = { name: a.name, typeId: a.typeId, active: true, timed: !!a.timed };
            }
          }
        });
        setActions(loaded);
        if (loadedCustom.length > 0) {
          setCustomActions(loadedCustom);
          setShowCustom(true);
        }
      } catch (err) {
        setError(t("createEvent.errLoad"));
      }
    };
    fetchEvent();
  }, [eventId, isEditMode]);

  const handleSlotClick = (index) => {
    const updated = [...actions];
    if (!updated[index].active) {
      updated[index].active = true;
      setActions(updated);
    }
    setActiveSlot(activeSlot === index ? null : index);
  };

  const handleNameChange = (index, value) => {
    const updated = [...actions];
    updated[index].name = value;
    setActions(updated);
  };

  const handleRemoveSlot = (index) => {
    const updated = [...actions];
    updated[index] = { name: "", typeId: actionsGPIOMap[index], active: false, timed: false };
    setActions(updated);
    if (activeSlot === index) setActiveSlot(null);
  };

  const handleTimedChange = (index, value) => {
    const updated = [...actions];
    updated[index].timed = value;
    setActions(updated);
  };

  const handleAddCustomAction = () => {
    const nextIndex = customActions.length;
    setCustomActions([...customActions, { name: "", typeId: `custom-${nextIndex}`, timed: false }]);
  };

  const handleCustomNameChange = (index, value) => {
    const updated = [...customActions];
    updated[index].name = value;
    setCustomActions(updated);
  };

  const handleCustomTimedChange = (index, value) => {
    const updated = [...customActions];
    updated[index].timed = value;
    setCustomActions(updated);
  };

  const handleRemoveCustomAction = (index) => {
    const updated = customActions.filter((_, i) => i !== index);
    const renumbered = updated.map((a, i) => ({ ...a, typeId: `custom-${i}` }));
    setCustomActions(renumbered);
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    const allActions = getAllActiveActions();
    if (allActions.length === 0) return;
    setTemplateSaving(true);
    try {
      await EventsService.createTemplate({
        name: templateName.trim(),
        category: category || "other",
        icon: templateIcon,
        isPublic: templatePublic,
        actions: allActions.map((a) => ({ name: a.name, typeId: a.typeId })),
      });
      setShowTemplateModal(false);
      setTemplateName("");
    } catch (err) {
      setError(t("createEvent.errTemplateSave", { message: err.message }));
    } finally {
      setTemplateSaving(false);
    }
  };

  const getAllActiveActions = () => {
    const gpioActive = actions.filter((a) => a.active && a.name.trim()).map((a) => ({ ...a, timed: !!a.timed }));
    const customActive = customActions.filter((a) => a.name.trim()).map((a) => ({ ...a, timed: !!a.timed }));
    return [...gpioActive, ...customActive];
  };

  const handleStart = async () => {
    setError(null);
    if (!eventName.trim()) {
      setError(t("createEvent.errName"));
      return;
    }

    const allActions = getAllActiveActions();
    if (allActions.length === 0) {
      setError(t("createEvent.errActions"));
      return;
    }

    setSaving(true);
    try {
      let id = eventId;
      if (!isEditMode) {
        const created = await EventsService.createEvent(eventName.trim());
        id = created.id;
      } else {
        await EventsService.renameEvent(id, eventName.trim());
      }
      await EventsService.updateEventActions(
        id,
        allActions.map((a) => ({ name: a.name, typeId: String(a.typeId), timed: !!a.timed }))
      );
      if (!isStarted) {
        await EventsService.startEvent(id);
      }
      navigate(`/event/${id}`);
    } catch (err) {
      setError(t("createEvent.errSave"));
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setError(null);
    if (!eventName.trim()) {
      setError(t("createEvent.errName"));
      return;
    }
    setSaving(true);
    try {
      let id = eventId;
      if (!isEditMode) {
        const created = await EventsService.createEvent(eventName.trim());
        id = created.id;
      } else {
        await EventsService.renameEvent(id, eventName.trim());
      }
      const allActions = getAllActiveActions();
      if (allActions.length > 0) {
        await EventsService.updateEventActions(
          id,
          allActions.map((a) => ({ name: a.name, typeId: String(a.typeId), timed: !!a.timed }))
        );
      }
      if (isStarted) {
        navigate(`/event/${id}`);
      } else {
        navigate("/my-events");
      }
    } catch (err) {
      setError(t("createEvent.errSaveDraft"));
      setSaving(false);
    }
  };

  const activeCount = getAllActiveActions().length;

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            <a
              id="sidebar-toggle"
              onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }}
            >
              <i className="fas fa-bars"></i>
            </a>
            <h5>{isEditMode ? t("createEvent.configureTitle") : t("createEvent.createTitle")}</h5>
          </div>
        </nav>
      </div>
      <div className="main-wrapper">
        <div className="create-event-container">
          {/* Event Name */}
          <div className="create-event-name-section">
            <label className="create-event-label">{t("createEvent.activityName")}</label>
            <input
              type="text"
              className="form-control create-event-name-input"
              placeholder={t("createEvent.activityPlaceholder")}
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Category & Tags */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            <div style={{ flex: "1 1 200px" }}>
              <label className="create-event-label">{t("createEvent.category")}</label>
              <select
                className="form-control"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "2 1 300px" }}>
              <label className="create-event-label">{t("createEvent.tags")}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t("createEvent.tagsPlaceholder")}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
              />
              {tags.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {tags.map((tag) => (
                    <span key={tag} className="tag-chip">
                      {tag}
                      <button
                        className="tag-chip-remove"
                        onClick={() => removeTag(tag)}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Device Mockup */}
          <div className="create-event-device-section">
            <label className="create-event-label">
              {t("createEvent.configureButtons")}
            </label>
            <p className="create-event-subtitle">
              {t("createEvent.configureButtonsDesc")}
            </p>

            <div className="device-mockup">
              <div className="device-frame">
                <div className="device-buttons-grid">
                  {actions.map((action, index) => (
                    <div key={index} className="device-button-slot">
                      <button
                        className={`device-button ${
                          action.active && action.name.trim()
                            ? "device-button-active"
                            : ""
                        } ${activeSlot === index ? "device-button-selected" : ""}`}
                        style={{
                          "--btn-color": actionsColorMap[index],
                          backgroundColor:
                            action.active && action.name.trim()
                              ? actionsColorMap[index]
                              : "rgba(255,255,255,0.08)",
                        }}
                        onClick={() => handleSlotClick(index)}
                      >
                        <span className="device-button-number">
                          {buttonLabels[index]}
                        </span>
                        {action.active && action.name.trim() && (
                          <span className="device-button-label">
                            {action.name}
                          </span>
                        )}
                      </button>

                      {/* Inline editor */}
                      <div
                        className={`device-button-editor ${
                          activeSlot === index ? "device-button-editor-open" : ""
                        }`}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("createEvent.actionLabelPlaceholder")}
                          value={action.name}
                          onChange={(e) =>
                            handleNameChange(index, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setActiveSlot(null);
                          }}
                        />
                        {action.active && (
                          <label className="timed-toggle">
                            <input
                              type="checkbox"
                              checked={!!action.timed}
                              onChange={(e) => handleTimedChange(index, e.target.checked)}
                            />
                            <span>{t("createEvent.timed")}</span>
                          </label>
                        )}
                        {action.active && (
                          <button
                            className="btn btn-sm btn-outline-danger device-button-remove"
                            onClick={() => handleRemoveSlot(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Custom Actions */}
          <div className="create-event-custom-section">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setShowCustom(!showCustom)}
              style={{ width: "100%", marginBottom: 12 }}
            >
              <i className={`fas fa-${showCustom ? "chevron-up" : "plus"}`} style={{ marginRight: 8 }}></i>
              {showCustom ? t("createEvent.hideCustomActions") : t("createEvent.addCustomActionsPhone")}
            </button>

            {showCustom && (
              <div className="custom-actions-panel">
                <p className="create-event-subtitle" style={{ marginBottom: 12, fontSize: 13 }}>
                  <i className="fas fa-mobile-alt" style={{ marginRight: 6 }}></i>
                  {t("createEvent.customActionsDesc")}
                </p>

                {customActions.map((ca, i) => (
                  <div key={i} className="custom-action-row">
                    <span
                      className="custom-action-dot"
                      style={{ backgroundColor: actionsColorMap[(4 + i) % actionsColorMap.length] }}
                    />
                    <span className="custom-action-label">C{i + 1}</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("createEvent.customActionPlaceholder", { index: i + 1 })}
                      value={ca.name}
                      onChange={(e) => handleCustomNameChange(i, e.target.value)}
                    />
                    <label className="timed-toggle">
                      <input
                        type="checkbox"
                        checked={!!ca.timed}
                        onChange={(e) => handleCustomTimedChange(i, e.target.checked)}
                      />
                      <span>{t("createEvent.timed")}</span>
                    </label>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveCustomAction(i)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleAddCustomAction}
                  style={{ marginTop: 8 }}
                >
                  <i className="fas fa-plus" style={{ marginRight: 6 }}></i>
                  {t("createEvent.addCustomAction")}
                </button>
              </div>
            )}
          </div>

          {/* Status + Actions */}
          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              <i
                className="fas fa-exclamation-circle"
                style={{ marginRight: "8px" }}
              ></i>
              {error}
            </div>
          )}

          <div className="create-event-status">
            <span className="create-event-status-text">
              {activeCount === 0
                ? t("createEvent.noActionsConfigured")
                : t("createEvent.actionsReady", { count: activeCount, plural: activeCount > 1 ? "s" : "" })}
            </span>
            {activeCount > 0 && (
              <div className="create-event-status-dots">
                {actions.map(
                  (a, i) =>
                    a.active &&
                    a.name.trim() && (
                      <span
                        key={`gpio-${i}`}
                        className="create-event-status-dot"
                        style={{ backgroundColor: actionsColorMap[i] }}
                      />
                    )
                )}
                {customActions.map(
                  (a, i) =>
                    a.name.trim() && (
                      <span
                        key={`custom-${i}`}
                        className="create-event-status-dot"
                        style={{ backgroundColor: actionsColorMap[(4 + i) % actionsColorMap.length] }}
                      />
                    )
                )}
              </div>
            )}
          </div>

          <div className="create-event-actions">
            <button
              className="btn btn-lg create-event-start-btn"
              onClick={handleStart}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ marginRight: 8 }}
                  ></span>
                  {isStarted ? t("createEvent.saving") : t("createEvent.starting")}
                </>
              ) : (
                <>
                  <i
                    className={`fas fa-${isStarted ? "check" : "play"}`}
                    style={{ marginRight: 8 }}
                  ></i>
                  {isStarted ? t("createEvent.saveChanges") : t("createEvent.startActivity")}
                </>
              )}
            </button>
            <button
              className="btn btn-lg create-event-draft-btn"
              onClick={handleSaveDraft}
              disabled={saving}
            >
              <i
                className="fas fa-save"
                style={{ marginRight: 8 }}
              ></i>
              {t("createEvent.saveDraft")}
            </button>
            <button
              className="btn btn-lg create-event-cancel-btn"
              onClick={() => navigate("/my-events")}
              disabled={saving}
            >
              {t("common.cancel")}
            </button>
          </div>

          {/* Save as Template */}
          {getAllActiveActions().length > 0 && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowTemplateModal(true)}
                style={{ width: "100%" }}
              >
                <i className="fas fa-bookmark" style={{ marginRight: 8 }}></i>
                {t("createEvent.saveAsTemplate")}
              </button>
            </div>
          )}

          <div className="create-event-warning">
            <i
              className="fas fa-info-circle"
              style={{ marginRight: 8 }}
            ></i>
            {isStarted
              ? t("createEvent.editWarning")
              : t("createEvent.draftWarning")}
          </div>
        </div>
      </div>

      {/* Template Save Modal */}
      {showTemplateModal && (
        <div className="alarm-modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="alarm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alarm-modal-header">
              <h6>{t("createEvent.saveAsTemplate")}</h6>
              <button className="alarm-modal-close" onClick={() => setShowTemplateModal(false)}>&times;</button>
            </div>
            <div className="alarm-modal-body">
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 6, display: "block" }}>
                  {t("createEvent.templateName")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("createEvent.templatePlaceholder")}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <div style={{ flex: "0 0 80px" }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 6, display: "block" }}>
                    {t("createEvent.icon")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={templateIcon}
                    onChange={(e) => setTemplateIcon(e.target.value)}
                    style={{ textAlign: "center", fontSize: 20 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="alarm-toggle" style={{ marginTop: 28 }}>
                    <input
                      type="checkbox"
                      checked={templatePublic}
                      onChange={(e) => setTemplatePublic(e.target.checked)}
                    />
                    <span>{t("createEvent.makePublic")}</span>
                  </label>
                </div>
              </div>
              <button
                className="btn btn-success"
                onClick={handleSaveAsTemplate}
                disabled={templateSaving || !templateName.trim()}
                style={{ width: "100%" }}
              >
                {templateSaving ? t("createEvent.saving") : t("createEvent.saveTemplate")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateEventPage;
